import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, GeoJSON, Popup } from 'react-leaflet';
import { createIcon } from "../../../utils/utils";
import "./LocationTrackMap.scss";
import { calculate_route } from "../../../services/shipments";
import { Spin } from "antd";
import CustomTileLayer from "../../ShipmentMap/CustomTileLayer/CustomTileLayer";
import dayjs from "dayjs";

const PopupItem = ({ label, value }) => (
  <div className="transit-popup__row">
    <span className="transit-popup__label">{label}:</span> <span>{value}</span>
  </div>
);

const MarkerWithPopup = ({ data }) => {
  if (!Array.isArray(data.coordinates) || data.coordinates.includes(null)) return null;

  return (
    <Marker
      position={data.coordinates}
      icon={createIcon(data.icon || "mix")}
    >
      <Popup>
        <div className="transit-popup">
          <div className="transit-popup__port">{data.title || 'Port'}</div>
          {data.popupItems && data.popupItems.map((item, itemIdx) => (
            <PopupItem key={`popup-item-${itemIdx}`} label={item.label} value={item.value || "N/A"} />
          ))}
        </div>
      </Popup>
    </Marker>
  );
};

const LocationTrackMap = ({ shipment }) => {
  const [geoJsonRoute, setGeoJsonRoute] = useState(null);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const response = await calculate_route(shipment.id);

        const routeCoordinates = response.data;

        const geoJsonData = {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: routeCoordinates.distance,
          },
          properties: {},
        };

        setGeoJsonRoute(geoJsonData);
      } catch (error) {
        console.error("Error fetching route:", error);
      }
    };

    fetchRoute();
  }, [shipment.pol.coordinates, shipment.pod.coordinates]);

  // Return loading state while fetching the route data
  if (!geoJsonRoute) return (
    <div className="LocationTrackMap">
      <Spin size="large" className="spinner" />
    </div>
  );

  const transitMarkers = shipment.transits.map(transit => (
    {
      title: transit.port,
      coordinates: transit.coordinates,
      icon: "mix",
      popupItems: [
        transit.arrival_date && {
          label: "Arrival",
          value: dayjs(transit.arrival_date).format("DD MMM YYYY"),
        },
        transit.departure_date && {
          label: "Departure",
          value: dayjs(transit.departure_date).format("DD MMM YYYY"),
        },
        transit.vessel && {
          label: "Vessel",
          value: transit.vessel || "N/A",
        },
      ].filter(Boolean)
    }
  ));

  const markers = [
    {
      title: "POL",
      coordinates: shipment.pol.coordinates,
      icon: "pol",
      popupItems: [
        shipment.pol.full_name && { label: "Port", value: shipment.pol.full_name },
        shipment.pol.date && { label: "ETD", value: shipment.pol.date },
      ]
    },
    {
      title: "Current Location",
      coordinates: [shipment.lat, shipment.lng],
      icon: "sea",
      popupItems: [
        shipment.pol.full_name && { label: "Port", value: shipment.pol.full_name },
        shipment.pol.date && { label: "ETD", value: shipment.pol.date },
      ]
    },
    {
      title: "POD",
      coordinates: shipment.pod.coordinates,
      icon: "pod",
      popupItems: [
        shipment.pol.full_name && { label: "Port", value: shipment.pod.full_name },
        shipment.pol.date && { label: "ETA", value: shipment.pod.date },
      ]
    },
    ...transitMarkers
  ]

  return (
    <div className="LocationTrackMap">
      <MapContainer center={[shipment.lat, shipment.lng]} zoom={3} scrollWheelZoom={false} style={{ height: 'inherit' }}>
        <CustomTileLayer maxZoom={6} minZoom={3} />
        {markers.map((marker, idx) => (
          <MarkerWithPopup key={`marker-${idx}`} data={marker} />
        ))}

        <GeoJSON data={geoJsonRoute} style={() => ({
          color: "white",
          weight: 4,
          opacity: 1,
        })} />

      </MapContainer>
    </div>
  );
};

export default LocationTrackMap;
